import React, { useMemo } from 'react'

// ─── Yonder atmosphere layer ───────────────────────────────────────────────────
// "Golden hour" drifting cloud + sun-sparkle texture that lives behind content
// on the deep dusk-to-sunset world. Pure CSS animation (transform/opacity only),
// capped at a small element budget, and respects prefers-reduced-motion via the
// .yonder-sparkle / .yonder-cloud rules in global.css.

interface AtmosphereProps {
  /** Total decorative elements (clouds + sparkles). Keep low — cheap by design. */
  density?: 'low' | 'medium'
  className?: string
}

export function Atmosphere({ density = 'medium', className = '' }: AtmosphereProps) {
  const sparkleCount = density === 'low' ? 10 : 18
  const cloudCount = density === 'low' ? 3 : 5

  const sparkles = useMemo(
    () =>
      Array.from({ length: sparkleCount }, (_, i) => ({
        id: i,
        top: Math.random() * 100,
        left: Math.random() * 100,
        size: 2 + Math.random() * 4,
        duration: 2.5 + Math.random() * 3,
        delay: Math.random() * 3,
      })),
    [sparkleCount]
  )

  const clouds = useMemo(
    () =>
      Array.from({ length: cloudCount }, (_, i) => ({
        id: i,
        top: 8 + Math.random() * 55,
        width: 140 + Math.random() * 180,
        height: 30 + Math.random() * 24,
        duration: 40 + Math.random() * 30,
        delay: Math.random() * 10,
      })),
    [cloudCount]
  )

  return (
    <div className={`yonder-atmosphere ${className}`} aria-hidden="true">
      {clouds.map((c) => (
        <div
          key={`cloud-${c.id}`}
          className="yonder-cloud"
          style={{
            top: `${c.top}%`,
            left: `-10%`,
            width: c.width,
            height: c.height,
            animationDuration: `${c.duration}s`,
            animationDelay: `${c.delay}s`,
          }}
        />
      ))}
      {sparkles.map((s) => (
        <div
          key={`sparkle-${s.id}`}
          className="yonder-sparkle"
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: s.size,
            height: s.size,
            animationDuration: `${s.duration}s`,
            animationDelay: `${s.delay}s`,
          }}
        />
      ))}
    </div>
  )
}
